<template>
  <div class="row">
    <div class="col-12">
      <div class="ads-page-header d-md-flex align-items-center justify-content-between mb-20">
        <div class="d-flex align-items-center">
          <span class="ads-page-icon">
            <i class="flaticon-placeholder"></i>
          </span>
          <div class="ms-12">
            <h4 class="mb-0 fw-bold">Gestion des villes</h4>
            <p class="mb-0 text-muted fs-13">
              {{ cities.length }} ville{{ cities.length > 1 ? "s" : "" }} au total
            </p>
          </div>
        </div>
      </div>

      <div class="card ads-card mb-25 border-0 letter-spacing">
        <div
          class="card-head d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25"
        >
          <div class="d-sm-flex align-items-center">
            <button
              class="ads-btn-primary position-relative transition border-0 fw-medium text-white d-inline-flex align-items-center me-10 mb-10 mb-lg-0"
              type="button"
              @click="openCreateModal"
              v-if="auth.can('SETTINGS')"
            >
              <i class="flaticon-plus position-relative fs-12 me-8"></i>
              Nouvelle ville
            </button>
            <button
              class="ads-btn-outline position-relative transition fw-medium d-inline-flex align-items-center mb-10 mb-lg-0"
              type="button"
              v-if="auth.can('SETTINGS')"
            >
              <i class="flaticon-file-1 position-relative fs-15 me-8"></i>
              Export
            </button>
          </div>
          <div class="ads-search position-relative">
            <i class="flaticon-search ads-search-icon"></i>
            <input
              type="text"
              class="form-control ps-40"
              v-model="searchTerm"
              placeholder="Rechercher une ville..."
            />
          </div>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25 pt-0">
          <EasyDataTable
            :headers="headers"
            :items="cities"
            :loading="loading"
            :search-value="searchTerm"
            :theme-color="themeColor"
            alternating
            table-class-name="ads-table"
            buttons-pagination
          >
            <template #loading>
              <div class="skeleton-table w-100">
                <div class="loading-spinner-wrap text-center py-15">
                  <div class="spinner-border text-success" role="status">
                    <span class="visually-hidden">Chargement...</span>
                  </div>
                </div>
                <div
                  class="skeleton-row d-flex align-items-center"
                  v-for="n in 6"
                  :key="n"
                >
                  <div
                    class="skeleton-cell"
                    v-for="c in headers.length"
                    :key="c"
                  >
                    <span class="skeleton-bar"></span>
                  </div>
                </div>
              </div>
            </template>

            <template #empty-message>
              <div class="empty-state text-center py-40">
                <i class="flaticon-placeholder fs-40 d-block mb-10 text-muted"></i>
                <p class="mb-0 text-muted">Aucune ville trouvée</p>
              </div>
            </template>

            <!-- Custom "active" column (status badge) -->
            <template #item-active="{ id, active }">
              <div class="switch">
                <input
                  type="checkbox"
                  class="switch-input"
                  :id="'switch-active-' + id"
                  :checked="active"
                  @change="toggleCityActive(id, $event.target.checked)"
                />
                <label class="switch-label" :for="'switch-active-' + id"></label>
              </div>
            </template>

            <template #item-stock="{ id, stock }">
              <div class="switch">
                <input
                  type="checkbox"
                  class="switch-input"
                  :id="'switch-stock-' + id"
                  :checked="stock"
                  @change="togglecitiestock(id, $event.target.checked)"
                />
                <label class="switch-label" :for="'switch-stock-' + id"></label>
              </div>
            </template>

            <template #item-hub="{ id, hub }">
              <div class="switch">
                <input
                  type="checkbox"
                  class="switch-input"
                  :id="'switch-hub-' + id"
                  :checked="hub"
                  @change="toggleZoneHub(id, $event.target.checked)"
                />
                <label class="switch-label" :for="'switch-hub-' + id"></label>
              </div>
            </template>

            <!-- Custom "actions" column -->
            <template #item-actions="{ id, item }">
              <div class="d-flex align-items-center gap-8" v-if="auth.can('SETTINGS')">
                <button
                  type="button"
                  class="ads-icon-btn ads-icon-btn-edit"
                  title="Modifier"
                  @click="openEditModal(id, item)"
                >
                  <i class="flaticon-pen"></i>
                </button>
                <button
                  type="button"
                  class="ads-icon-btn ads-icon-btn-delete"
                  title="Supprimer"
                  @click="deleteCity(id)"
                >
                  <i class="flaticon-delete"></i>
                </button>
              </div>
            </template>
          </EasyDataTable>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal -->
  <div
    class="modal fade"
    id="createnewCityModal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content ads-modal-content">
        <div class="modal-header ads-modal-header">
          <div class="d-flex align-items-center">
            <span class="ads-modal-icon">
              <i class="flaticon-placeholder"></i>
            </span>
            <h5 class="modal-title ms-12 mb-0">
              {{ isEditing ? "Modifier la ville" : "Nouvelle ville" }}
            </h5>
          </div>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="submitCity">
            <div class="row g-10">
              <!-- Zone -->
              <div class="col-md-6">
                <div class="form-group mb-20">
                  <label class="fw-medium mb-10 d-block">La zone de la ville</label>
                  <v-select
                    class="custom-select-height"
                    v-model="newCity.id_zone"
                    label="name"
                    :options="zones"
                  />
                </div>
              </div>

              <!-- Code -->
              <div class="col-md-6">
                <div class="form-group mb-20">
                  <label class="fw-medium mb-10 d-block">Référence de la ville</label>
                  <input type="text" v-model="newCity.code" class="form-control" />
                </div>
              </div>

              <!-- Nom -->
              <div class="col-12">
                <div class="form-group mb-20">
                  <label class="fw-medium mb-10 d-block">Nom de la ville</label>
                  <input type="text" v-model="newCity.name" class="form-control" />
                </div>
              </div>

              <!-- Fees -->
              <div
                class="col-md-6"
                v-for="(field, key) in feeFields"
                :key="key"
              >
                <div class="form-group mb-20">
                  <label class="fw-medium mb-10 d-block">{{ field.label }}</label>
                  <input
                    type="number"
                    v-model="newCity[key]"
                    class="form-control"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>

        <div class="modal-footer ads-modal-footer">
          <button
            type="button"
            class="ads-btn-outline transition fw-medium"
            data-bs-dismiss="modal"
          >
            Fermer
          </button>
          <button type="button" @click="submitCity" class="ads-btn-primary border-0 transition fw-medium">
            {{ isEditing ? "Mettre à jour" : "Enregistrer" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from "@/stores/auth";
import api from "@/utils/axios";
import { Modal } from "bootstrap";
import { onMounted, reactive, ref } from "vue";
import { useAppToast } from "@/composables/useAppToast";
import Swal from "sweetalert2";

export default {
  name: "CitiesContent",
  setup: () => {
    const { showToast } = useAppToast();
    const themeColor = "#06b48a";
    const searchTerm = ref("");

    const newCity = reactive({
      code: "",
      name: "",
      id_zone: null,
      delivered_fees: 0,
      delivered_same_city_fees: 0,
      delivered_same_zone_fees: 0,
      delivered_out_of_zone_fees: 0,
      retourned_fees: 0,
      refused_fees: 0,
    });
    const auth = useAuthStore();
    const isEditing = ref(false);
    const headers = [
      { text: "Code", value: "code" },
      { text: "Nom", value: "name" },
      { text: "Zone", value: "zonename" },
      { text: "Frais de livraison", value: "delivered_fees" },
      { text: "FL(même ville)", value: "delivered_same_city_fees" },
      { text: "FL(même zone)", value: "delivered_same_zone_fees" },
      { text: "FL(hors zone)", value: "delivered_out_of_zone_fees" },
      { text: "Frais Retour", value: "retourned_fees" },
      { text: "Frais Refus", value: "refused_fees" },
      { text: "Statut", value: "active" },
      { text: "Actions", value: "actions" },
    ];

    const feeFields = {
      delivered_fees: { label: "Frais de livraison" },
      retourned_fees: { label: "Frais de retour" },
      refused_fees: { label: "Frais de refus" },
      delivered_same_city_fees: { label: "Frais livraison (Même Ville)" },
      delivered_same_zone_fees: { label: "Frais livraison (Même Zone)" },
      delivered_out_of_zone_fees: { label: "Frais livraison (Hors Zone)" },
    };

    const cities = ref([]);
    const zones = ref([]);
    const loading = ref(true);

    onMounted(async () => {
      fetchCities();
      fetchZones();
    });

    function resetForm() {
      Object.assign(newCity, {
        id: null,
        id_zone: "",
        code: "",
        name: "",
        delivered_fees: "",
        retourned_fees: "",
        refused_fees: "",
        delivered_same_city_fees: "",
        delivered_same_zone_fees: "",
        delivered_out_of_zone_fees: "",
      });
    }

    function openCreateModal() {
      isEditing.value = false;
      resetForm();

      const modalEl = document.getElementById("createnewCityModal");
      const modal = new Modal(modalEl);
      modal.show();
    }

    function openEditModal(id, city) {
      const rowCity = cities.value.find((c) => c.id === id);
      if (rowCity) {
        isEditing.value = true;
        Object.assign(newCity, rowCity);

        const modalEl = document.getElementById("createnewCityModal");
        const modal = new Modal(modalEl);
        modal.show();
      }
    }

    async function submitCity() {
      if (!newCity.code || !newCity.name) {
        showToast("Please fill in all fields!", "error");
        return;
      }

      if (isEditing.value) {
        try {
          // Example: send to API
          const response = await api.post("/api/auth/city_edit/" + newCity.id, {
            code: newCity.code,
            name: newCity.name,
            id_zone: newCity.id_zone?.id,
            delivered_fees: newCity.delivered_fees,
            delivered_same_city_fees: newCity.delivered_same_city_fees,
            delivered_same_zone_fees: newCity.delivered_same_zone_fees,
            delivered_out_of_zone_fees: newCity.delivered_out_of_zone_fees,
            retourned_fees: newCity.retourned_fees,
            refused_fees: newCity.refused_fees,
          });

          if (response.data.message == "success") {
            const modalEl = document.getElementById("createnewCityModal");
            const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
            modal.hide();

            resetForm();
            // get cities list refresh
            fetchCities();
            showToast("Zone added successfully!", "success");
          } else {
            showToast("Error: " + response.data.message, "error");
          }
        } catch (error) {
          console.error(error);
          showToast("An error occurred while adding the city.", "error");
        }
        console.log("Editing city:", newCity);
      } else {
        try {
          // Example: send to API
          const response = await api.post("/api/auth/city_add", {
            code: newCity.code,
            name: newCity.name,
            zone_id: newCity.id_zone.id,
            fees: newCity.delivered_fees,
            same_city: newCity.delivered_same_city_fees,
            same_zone: newCity.delivered_same_zone_fees,
            out_of_zone: newCity.delivered_out_of_zone_fees,
            retourned_fees: newCity.retourned_fees,
            refused_fees: newCity.refused_fees,
          });

          if (response.data.message == "success") {
            const modalEl = document.getElementById("createnewCityModal");
            const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
            modal.hide();

            resetForm();
            // get cities list refresh
            fetchCities();
            showToast("Zone added successfully!", "success");
          } else {
            showToast("Error: " + response.data.message, "error");
          }
        } catch (error) {
          console.error(error);
          showToast("An error occurred while adding the city.", "error");
        }
      }
    }

    async function fetchCities() {
      loading.value = true;
      try {
        const response = await api.get("/api/auth/city_list");
        if (response && response.data) {
          // handle the fetched cities data
          // console.log("Fetched cities:", response.data);
          cities.value = response.data;
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
      } finally {
        loading.value = false;
      }
    }
    async function fetchZones() {
      try {
        const response = await api.get("/api/auth/ListZone");
        if (response && response.data) {
          // handle the fetched cities data
          // console.log("Fetched cities:", response.data);
          zones.value = response.data;
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    }
    async function toggleCityActive(id, value) {
      const city = cities.value.find((z) => z.id === id);
      if (city) {
        city.active = value;
        // Optionally, send update to API here
        const res = await api.post(`/api/auth/city_active/${id}`, {
          active: value,
        });
        if(res.message =='success'){
          showToast('Ville est bien modifiée',"success");
        }
        // console.log("Zone active status updated:", res.data);
      }
    }
    async function deleteCity(id) {
      Swal.fire({
        text: "Vous voulez supprimer cette ville ?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Oui, supprimer!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await api.post(`/api/auth/destroyCity/${id}`);
            if (response.data.message === "success") {
              // Remove the city from the local list
              fetchCities();
              showToast("City deleted successfully!", "success");
            } else {
              showToast(
                "Error deleting city: " + response.data.message,
                "error"
              );
            }
          } catch (error) {
            console.error("Error deleting city:", error);
            showToast("An error occurred while deleting the city.", "error");
          }
        }
      });
    }

    return {
      newCity,
      submitCity,
      headers,
      cities,
      zones,
      loading,
      themeColor,
      searchTerm,
      toggleCityActive,
      openCreateModal,
      openEditModal,
      isEditing,
      feeFields,
      deleteCity,
      auth,
    };
  },
};
</script>

<style scoped>
/* ---------- Page header ---------- */
.ads-page-icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 180, 138, 0.1);
  color: #06b48a;
  font-size: 20px;
}

/* ---------- Card ---------- */
.ads-card {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(43, 42, 63, 0.06);
}

.ads-card .card-head {
  border-bottom: 1px solid #f0f0f5;
}

/* ---------- Buttons ---------- */
.ads-btn-primary {
  background: #06b48a;
  padding: 11px 22px;
  border-radius: 8px;
  font-size: 14px;
}

.ads-btn-primary:hover {
  background: #059c78;
  color: #fff;
}

.ads-btn-outline {
  background: transparent;
  border: 1px solid #e2e2ea;
  color: #2b2a3f;
  padding: 10px 22px;
  border-radius: 8px;
  font-size: 14px;
}

.ads-btn-outline:hover {
  background: #f8f8fa;
  border-color: #d5d5e0;
}

/* ---------- Search ---------- */
.ads-search {
  min-width: 260px;
}

.ads-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #a2a1bb;
  font-size: 14px;
}

.ads-search .form-control {
  border-radius: 8px;
  border: 1px solid #e2e2ea;
  height: 44px;
}

.ads-search .form-control:focus {
  border-color: #06b48a;
  box-shadow: 0 0 0 3px rgba(6, 180, 138, 0.12);
}

/* ---------- Table ---------- */
:deep(.ads-table) {
  --easy-table-border: none;
  --easy-table-row-border: 1px solid #f1f1f5;

  --easy-table-header-font-size: 12px;
  --easy-table-header-height: 50px;
  --easy-table-header-font-color: #8e8da1;
  --easy-table-header-background-color: #f8f8fb;
  --easy-table-header-item-padding: 12px 16px;

  --easy-table-body-row-font-size: 14px;
  --easy-table-body-row-height: 60px;
  --easy-table-body-row-font-color: #2b2a3f;
  --easy-table-body-row-background-color: #ffffff;
  --easy-table-body-item-padding: 12px 16px;

  --easy-table-body-row-hover-font-color: #2b2a3f;
  --easy-table-body-row-hover-background-color: #f8fbfa;

  --easy-table-body-even-row-font-color: #2b2a3f;
  --easy-table-body-even-row-background-color: #fbfbfd;

  --easy-table-footer-font-color: #8e8da1;
  --easy-table-footer-font-size: 13px;
  --easy-table-footer-background-color: #ffffff;

  --easy-table-message-font-color: #8e8da1;

  --easy-table-loading-mask-background-color: #ffffff;
}

:deep(.ads-table th) {
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* ---------- Status pills ---------- */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
}

.status-pill-info {
  background: rgba(31, 177, 230, 0.1);
  color: #1fb1e6;
}

/* ---------- Action icon buttons ---------- */
.ads-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #eceef1;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #8e8da1;
  transition: all 0.2s ease;
}

.ads-icon-btn-edit:hover {
  background: rgba(101, 96, 240, 0.1);
  border-color: transparent;
  color: #6560f0;
}

.ads-icon-btn-delete:hover {
  background: rgba(239, 41, 41, 0.1);
  border-color: transparent;
  color: #ef2929;
}

/* ---------- Modal ---------- */
.ads-modal-content {
  border: none;
  border-radius: 14px;
  overflow: hidden;
}

.ads-modal-header {
  background: #f8f8fb;
  border-bottom: 1px solid #f0f0f5;
  padding: 18px 25px;
}

.ads-modal-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(6, 180, 138, 0.1);
  color: #06b48a;
}

.ads-modal-footer {
  border-top: 1px solid #f0f0f5;
  padding: 16px 25px;
}

.ads-modal-footer .ads-btn-primary,
.ads-modal-footer .ads-btn-outline {
  padding: 9px 20px;
}

/* ---------- Loading skeleton ---------- */
.skeleton-table {
  padding: 10px 0;
}

.skeleton-row {
  padding: 12px 0;
  border-bottom: 1px solid #f1f1f1;
  gap: 20px;
}

.skeleton-cell {
  flex: 1;
}

.skeleton-bar {
  display: block;
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, #eef0f2 25%, #e2e5e9 37%, #eef0f2 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.empty-state {
  width: 100%;
}
</style>
